import { Component, type ErrorInfo, type ReactNode } from "react";
import { Sentry } from "@/lib/sentry";
import {
  isChunkLoadError,
  tryRecoverFromChunkLoadError,
} from "@/lib/chunk-load-recovery";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  // True only when we've actually initiated a reload and are waiting
  // for the page to navigate away. Not just "this is a chunk error" —
  // if recovery is unavailable (guard hit / already reloaded) we fall
  // through to the normal fallback so genuine issues remain loud.
  isReloading: boolean;
}

// App-shell error boundary. Catches render-phase errors anywhere below
// it, ships them to Sentry (no-op when disabled), and shows a recovery
// screen so a single buggy component doesn't leave the user staring
// at a blank white page.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isReloading: false };

  static getDerivedStateFromError(error: unknown): State {
    // If this is a chunk-load failure and we can still attempt a
    // one-shot reload, switch into the transitional "Updating…" UI.
    // Otherwise fall through to the regular error fallback (including
    // for repeated/unrecoverable chunk-load failures, so the user
    // isn't stuck on a spinner forever).
    if (isChunkLoadError(error) && tryRecoverFromChunkLoadError(error)) {
      return { hasError: true, isReloading: true };
    }
    return { hasError: true, isReloading: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (this.state.isReloading) {
      // Recovery in flight — report as low-severity so it's visible
      // without polluting the error feed, then leave it alone.
      try {
        Sentry.captureMessage("chunk-load auto-recovery: error boundary", {
          level: "info",
          tags: { kind: "chunk-load-recovery" },
          extra: {
            message: error.message,
            componentStack: info.componentStack,
          },
        });
      } catch {
        /* ignore */
      }
      return;
    }
    if (isChunkLoadError(error)) {
      // Recovery unavailable (e.g. we already reloaded once) — log as a
      // distinct warning so we can tell these apart from genuine
      // render-phase bugs, but still show the normal red fallback so
      // the user has an obvious next action.
      try {
        Sentry.captureMessage("chunk-load failure after recovery reload", {
          level: "warning",
          tags: { kind: "chunk-load-recovery-failed" },
          extra: {
            message: error.message,
            componentStack: info.componentStack,
          },
        });
      } catch {
        /* ignore */
      }
      console.error("ErrorBoundary caught (post-recovery chunk):", error, info.componentStack);
      return;
    }
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
    });
    // Also log so dev sees it in the console without needing Sentry.
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    if (this.state.isReloading) {
      return (
        <div
          className="flex h-screen flex-col items-center justify-center gap-3 bg-background px-6 text-center"
          role="status"
          aria-live="polite"
        >
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" />
          <p className="text-sm text-muted-foreground">
            Updating to the latest version…
          </p>
        </div>
      );
    }

    if (this.props.fallback !== undefined) return this.props.fallback;

    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <h1 className="text-2xl font-semibold">Something went wrong.</h1>
        <p className="max-w-md text-muted-foreground">
          We&apos;ve been notified and are looking into it. Please reload the page to try again.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          className="mt-2 rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
        >
          Reload
        </button>
      </div>
    );
  }
}
